import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  Dialog,
  EmptyState,
  Headline,
  Loading,
  Refusal,
} from "@lopie/kernel-ui";

import { parseHash } from "../hash.js";
import {
  STATE_FILTERS,
  approveCopy,
  declineCopy,
  emptyCopy,
  outcomeOf,
  whenSentence,
  whyDisabled,
} from "./accessQueue.js";

/**
 * Access requests: who is waiting for a tag, and should they have it (LO-210).
 *
 * A person who opens an app and lacks a tag it needs gets a screen that says
 * what is missing, with a button to ask for it. That ask lands here, for the
 * people who may grant the tag. Approving performs the grant through the same
 * governed route as the People screen, as the person approving, with this
 * request as its reason; declining closes it with a reason and grants nothing.
 *
 * ## What is rendered from where
 *
 * Every word of who, which app, which tag, the note and when is the kernel's
 * row. Whether the buttons are on is the kernel's answer too (`may_decide` for
 * the person, `can_decide` for this tag in this app), and when they are off the
 * reason is on the screen beside them rather than in a tooltip a keyboard
 * cannot reach. No tag is named in this file.
 *
 * ## Why you see what you see
 *
 * People who may grant tags see every request; anybody else sees only their
 * own. The kernel says which and why (`scope_reason`) and it is the first thing
 * under the figure, because a list the length it is for a reason the reader
 * does not know is a list nobody can interpret.
 *
 * ## The caveat is in the dialog
 *
 * Approving is the most security-relevant write this console makes. What it
 * does and does not prove is said in the dialog that confirms it, once, at the
 * moment somebody is about to do it, and again from the kernel's own list in
 * the banner after.
 */

const STATE_WORD = { pending: "Waiting", approved: "Approved", declined: "Declined" };
// The colour frames the chip and the word carries the state, in the ordinary
// text colour: the kernel's semantic colours are for a border and a rule, and
// as text on white they fall short of 4.5:1.
const STATE_FRAME = { pending: "kernel.attention", approved: "kernel.permitted", declined: "kernel.stale" };

function Mono({ children }) {
  return (
    <Typography component="span" variant="body2"
      sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
      {children}
    </Typography>
  );
}

function RequestCard({ request, decision, governors, focused, busy, onDecide }) {
  const ref = useRef(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: "center" });
  }, [focused]);
  const pending = request.state === "pending";
  const why = pending ? whyDisabled(decision, request, governors) : "";
  const when = whenSentence(request.requested_at);
  return (
    <Paper
      ref={ref}
      variant="outlined"
      data-request-id={request.id}
      aria-current={focused ? "true" : undefined}
      sx={{
        p: 2,
        minWidth: 0,
        ...(focused ? { outline: 2, outlineStyle: "solid", outlineColor: "primary.main" } : {}),
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography component="div" variant="subtitle1"
            sx={{ fontWeight: 600, overflowWrap: "anywhere", flex: "1 1 14ch" }}>
            {request.requested_by}
          </Typography>
          <Chip size="small" variant="outlined"
            sx={{ color: "text.primary", borderColor: STATE_FRAME[request.state] || "divider" }}
            label={STATE_WORD[request.state] || request.state} />
        </Stack>

        <Typography variant="body2">
          Asked for the <Mono>{request.tag}</Mono> tag through{" "}
          <Mono>{request.app_type}</Mono>
          {when ? (
            <>
              {" · "}
              <Box component="span" title={request.requested_at}>{when}</Box>
            </>
          ) : null}
        </Typography>

        {request.note ? (
          <Typography variant="body2" color="text.secondary"
            sx={{ borderLeft: 2, borderColor: "divider", pl: 1.5, overflowWrap: "anywhere" }}>
            {request.requested_by.split("@")[0]} wrote: {request.note}
          </Typography>
        ) : null}

        {!pending ? (
          <Typography variant="body2" color="text.secondary">
            {STATE_WORD[request.state] || request.state}
            {request.decided_by ? <> by <Mono>{request.decided_by}</Mono></> : null}
            {request.decided_at ? <> · {whenSentence(request.decided_at)}</> : null}
            {request.reason ? <>. Reason: {request.reason}</> : "."}
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Button size="small" variant="contained"
                disabled={Boolean(why) || busy}
                onClick={() => onDecide("approve", request)}>
                Approve
              </Button>
              <Button size="small" variant="outlined"
                disabled={Boolean(why) || busy}
                onClick={() => onDecide("decline", request)}>
                Decline
              </Button>
            </Stack>
            {why ? (
              <Typography variant="caption" color="text.secondary" data-why-disabled>
                {why}
              </Typography>
            ) : null}
            {request.routed_to?.length ? (
              <Typography variant="caption" color="text.secondary">
                Routed to {request.routed_to.join(", ")}, who may grant it.
              </Typography>
            ) : null}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function DecideDialog({ pending, busy, failure, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [pending?.request?.id, pending?.action]);
  const open = Boolean(pending);
  const request = pending?.request;
  const approving = pending?.action === "approve";
  const copy = request ? (approving ? approveCopy(request) : declineCopy(request)) : null;
  const tooLong = reason.length > 500;
  const missing = !approving && !reason.trim();
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth
      aria-labelledby="access-request-title">
      {copy ? (
        <>
          <DialogTitle id="access-request-title">{copy.title}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              {approving && copy.note ? (
                <Typography variant="body2"
                  sx={{ borderLeft: 2, borderColor: "divider", pl: 1.5, overflowWrap: "anywhere" }}>
                  Their note: {copy.note}
                </Typography>
              ) : null}
              {copy.lines.map((line) => (
                <Typography key={line} variant="body2">{line}</Typography>
              ))}
              {!approving ? (
                <TextField
                  label={copy.reasonLabel}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  minRows={2}
                  required
                  autoFocus
                  error={tooLong}
                  helperText={tooLong ? "At most 500 characters." : `${reason.length} of 500`}
                  disabled={busy}
                />
              ) : null}
              {failure ? (
                <Alert severity="error" variant="outlined">
                  <AlertTitle>The kernel refused that</AlertTitle>
                  {failure.body?.detail || failure.message}
                  {failure.body?.still_pending ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {failure.body.still_pending}
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel} disabled={busy}>
              {failure ? "Close" : "Cancel"}
            </Button>
            <Button variant="contained" disabled={busy || missing || tooLong || Boolean(failure?.status === 409)}
              onClick={() => onConfirm(approving ? {} : { reason: reason.trim() })}>
              {busy ? "Working…" : copy.confirm}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}

export default function AccessRequests({ get, post, onEffective }) {
  const [state, setState] = useState("pending");
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // { action, request }
  const [busy, setBusy] = useState(false);
  const [decideFailure, setDecideFailure] = useState(null);
  const [outcome, setOutcome] = useState(null);
  // The request a mark on an access list sent somebody to, if any.
  const [asked] = useState(() => parseHash(window.location.hash).params.get("request") || "");

  const load = useCallback(async (which = state) => {
    setLoading(true);
    try {
      setData(await get("/access-requests", { state: which }));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    } finally {
      setLoading(false);
    }
  }, [get, state]);

  useEffect(() => {
    load(state);
    // Tags do not govern this screen: it lists requests, not records.
    onEffective?.(null);
  }, [load, state, onEffective]);

  async function decide(body) {
    setBusy(true);
    setDecideFailure(null);
    const { action, request } = deciding;
    try {
      const result = await post(`/access-requests/${encodeURIComponent(request.id)}/${action}`, body);
      setDeciding(null);
      setOutcome({ action, body: result });
      await load(state);
    } catch (e) {
      setDecideFailure(e);
      // Somebody else may have decided it first: show the queue as it is now,
      // behind the refusal, rather than leaving a card that is not true.
      if (e?.status === 409) await load(state);
    } finally {
      setBusy(false);
    }
  }

  if (failure && !data) {
    return <Refusal failure={failure} actor="This console" onRetry={() => load(state)} />;
  }
  if (loading && !data) return <Loading label="Reading the access requests" />;

  const items = data?.items || [];
  const scope = data?.scope || "own";
  const governors = data?.governance?.governors || [];
  const filter = STATE_FILTERS.find((f) => f.id === state) || STATE_FILTERS[0];
  const out = outcome ? outcomeOf(outcome.action, outcome.body) : null;
  const empty = emptyCopy({ scope, state });
  const lost = asked && !loading && !items.some((r) => r.id === asked);

  return (
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow={scope === "all" ? "Access requests" : "Your access requests"}
        figure={String(items.length)}
        caption={state === "pending"
          ? `${items.length === 1 ? "person is" : "people are"} waiting for a tag`
          : `${filter.label.toLowerCase()} request${items.length === 1 ? "" : "s"}`}
        basis={"What the kernel holds in this state as of this read, newest first. "
          + "Not a forward count: a person can ask at any moment, and an app can "
          + "be opened by somebody who never asks."}
        lede={data?.scope_reason}
      />

      {out ? (
        <Alert severity={out.severity} variant="outlined" onClose={() => setOutcome(null)}>
          <AlertTitle>{out.title}</AlertTitle>
          <Stack spacing={0.5}>
            {out.lines.map((line) => (
              <Typography key={line} variant="body2">{line}</Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      {lost ? (
        <Alert severity="info" variant="outlined">
          The request you were sent to is not in this list. It may have been
          decided since, in which case it is under Approved or Declined.
        </Alert>
      ) : null}

      <ToggleButtonGroup
        exclusive
        size="small"
        value={state}
        onChange={(_, next) => { if (next) setState(next); }}
        aria-label="Which requests to show"
        sx={{ flexWrap: "wrap" }}
      >
        {STATE_FILTERS.map((f) => (
          <ToggleButton key={f.id} value={f.id}>{f.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {failure ? (
        <Refusal failure={failure} actor="This console" onRetry={() => load(state)} />
      ) : null}

      {items.length ? (
        <Stack spacing={1.5} component="ul" aria-label="Access requests"
          sx={{ listStyle: "none", m: 0, p: 0 }}>
          {items.map((request) => (
            <Box component="li" key={request.id} sx={{ minWidth: 0 }}>
              <RequestCard
                request={request}
                decision={{ may_decide: Boolean(data?.may_decide), can_decide: Boolean(request.can_decide) }}
                governors={governors}
                focused={asked === request.id}
                busy={busy}
                onDecide={(action, r) => { setDecideFailure(null); setDeciding({ action, request: r }); }}
              />
            </Box>
          ))}
        </Stack>
      ) : loading ? null : (
        <EmptyState title={empty.title}>
          <Typography variant="body2">{empty.text}</Typography>
        </EmptyState>
      )}

      <DecideDialog
        pending={deciding}
        busy={busy}
        failure={decideFailure}
        onCancel={() => { setDeciding(null); setDecideFailure(null); }}
        onConfirm={decide}
      />
    </Stack>
  );
}
